import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  BASE_URL_SANDBOX,
  GET_VARIENTS_DETAIL,
} from '../../../Constants/Config';
import {
  applyPriceBookToItems,
  CHECKOUT_PRICE_MAX_AGE_MS,
} from '../../../services/priceBook/priceBookStore';

const initialState = {
  loading: false,
  currentMerchantId: null,
  variantData: null,
  variantCartData: {
    loading: false,
    pendingRequests: 0,
    pendingByMerchant: {},
    errorsByMerchant: {},
    latestRequestIdByKey: {},
    data: [],
    error: '',
  },
  error: '',
};

const variantDetailRequests = new Map();

const getVariantNetworkRequestKey = (data) =>
  JSON.stringify(
    Object.keys(data || {})
      .sort()
      .map((key) => [key, String(data[key] ?? '')]),
  );

const normalizeVariantKeyPart = (value) => String(value ?? '').trim();

const normalizeVariantMerchantId = (value) =>
  normalizeVariantKeyPart(value).toUpperCase();

export const CART_VARIANT_REQUEST_SOURCE = 'cart';

export const createVariantRequestKey = (variant) => {
  const merchantId = normalizeVariantMerchantId(variant?.merchant_id);
  const productId = normalizeVariantKeyPart(variant?.product_id);
  const variantName = normalizeVariantKeyPart(variant?.variant);

  return merchantId && productId && variantName
    ? JSON.stringify([merchantId, productId, variantName])
    : '';
};

const upsertVariantCartData = (state, variant) => {
  const variantKey = createVariantRequestKey(variant);
  if (
    !variantKey ||
    variant?.id === null ||
    variant?.id === undefined
  ) {
    return;
  }

  const existingItemIndex = state.variantCartData.data.findIndex(
    (item) => createVariantRequestKey(item) === variantKey,
  );

  if (existingItemIndex === -1) {
    state.variantCartData.data.push(variant);
    return;
  }

  state.variantCartData.data[existingItemIndex] = variant;
};

const setMerchantContext = (state, merchantId) => {
  const normalizedMerchantId = normalizeVariantMerchantId(merchantId);
  if (!normalizedMerchantId || state.currentMerchantId === normalizedMerchantId) {
    return;
  }

  state.currentMerchantId = normalizedMerchantId;
  state.loading = false;
  state.variantData = null;
  state.variantCartData.loading = false;
  state.variantCartData.pendingRequests = 0;
  state.variantCartData.pendingByMerchant = {};
  state.variantCartData.errorsByMerchant = {};
  state.variantCartData.latestRequestIdByKey = {};
  state.error = '';
  state.variantCartData.error = '';
};

// Generate pening , fulfilled and rejected action type
export const fetchVariantData = createAsyncThunk(
  'variant/fetchVariantData',
  async (data, { rejectWithValue }) => {
    const requestData = {
      merchant_id: data?.merchant_id,
      product_id: data?.product_id,
      variant: data?.variant,
    };

    if (
      !requestData.merchant_id ||
      !requestData.product_id ||
      !requestData.variant
    ) {
      return rejectWithValue({ message: 'Missing variant request details' });
    }

    try {
      const requestKey = getVariantNetworkRequestKey(requestData);
      let request = variantDetailRequests.get(requestKey);

      if (!request) {
        request = axios
          .post(BASE_URL_SANDBOX + GET_VARIENTS_DETAIL, requestData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 10000,
          })
          .then(async (response) => {
            const variant = response?.data?.result;
            if (
              !variant ||
              typeof variant !== 'object' ||
              Array.isArray(variant) ||
              variant.id === undefined ||
              variant.id === null ||
              variant.product_id === undefined ||
              variant.product_id === null ||
              !String(variant.variant ?? '').trim()
            ) {
              throw new Error('This product option is unavailable');
            }

            const [pricedVariant] = await applyPriceBookToItems(
              requestData.merchant_id,
              [variant],
              data?.requestSource === CART_VARIANT_REQUEST_SOURCE
                ? { batch: true, maxAgeMs: CHECKOUT_PRICE_MAX_AGE_MS }
                : { mode: 'single', maxAgeMs: 0 },
            );
            return {
              ...pricedVariant,
              merchant_id: pricedVariant.merchant_id || requestData.merchant_id,
            };
          });
        variantDetailRequests.set(requestKey, request);
      }

      try {
        return await request;
      } finally {
        if (variantDetailRequests.get(requestKey) === request) {
          variantDetailRequests.delete(requestKey);
        }
      }
    } catch (error) {
      return rejectWithValue({
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Unable to retrieve this product option',
      });
    }
  },
);

const getRequestMerchantId = (action) =>
  normalizeVariantMerchantId(action.meta?.arg?.merchant_id);

const isCartVariantRequest = (action) =>
  action.meta.arg?.requestSource === CART_VARIANT_REQUEST_SOURCE;

const isLatestVariantRequest = (state, action) => {
  const requestKey = createVariantRequestKey(action.meta.arg);
  return (
    Boolean(requestKey) &&
    state.variantCartData.latestRequestIdByKey?.[requestKey] ===
      action.meta.requestId
  );
};

const decrementMerchantPendingRequests = (state, merchantId) => {
  if (!merchantId) return;
  if (!state.variantCartData.pendingByMerchant) {
    state.variantCartData.pendingByMerchant = {};
  }

  const pendingRequests = Math.max(
    0,
    Number(state.variantCartData.pendingByMerchant[merchantId] || 0) - 1,
  );

  if (pendingRequests === 0) {
    delete state.variantCartData.pendingByMerchant[merchantId];
  } else {
    state.variantCartData.pendingByMerchant[merchantId] = pendingRequests;
  }
};

const variantSlice = createSlice({
  name: 'variant',
  initialState,
  reducers: {
    cacheVariantDetail: (state, action) => {
      upsertVariantCartData(state, action.payload);
    },
    setVariantMerchantContext: (state, action) => {
      setMerchantContext(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchVariantData.pending, (state, action) => {
      const merchantId = getRequestMerchantId(action);
      setMerchantContext(state, merchantId);
      const requestKey = createVariantRequestKey(action.meta?.arg);
      const cartRequest = isCartVariantRequest(action);
      if (!state.variantCartData.pendingByMerchant) {
        state.variantCartData.pendingByMerchant = {};
      }
      if (!state.variantCartData.errorsByMerchant) {
        state.variantCartData.errorsByMerchant = {};
      }
      if (!state.variantCartData.latestRequestIdByKey) {
        state.variantCartData.latestRequestIdByKey = {};
      }
      if (cartRequest && merchantId) {
        const merchantPendingRequests = Number(
          state.variantCartData.pendingByMerchant[merchantId] || 0,
        );
        if (merchantPendingRequests === 0) {
          delete state.variantCartData.errorsByMerchant[merchantId];
        }
        state.variantCartData.pendingByMerchant[merchantId] =
          merchantPendingRequests + 1;
      }
      state.variantCartData.pendingRequests =
        Number(state.variantCartData.pendingRequests || 0) + 1;
      state.loading = state.variantCartData.pendingRequests > 0;
      state.variantCartData.loading = state.variantCartData.pendingRequests > 0;
      state.variantCartData.error = '';
      if (requestKey) {
        state.variantCartData.latestRequestIdByKey[requestKey] =
          action.meta.requestId;
      }
    });
    builder.addCase(fetchVariantData.fulfilled, (state, action) => {
      const merchantId = getRequestMerchantId(action);
      if (merchantId && state.currentMerchantId !== merchantId) return;

      const latestRequest = isLatestVariantRequest(state, action);
      const cartRequest = isCartVariantRequest(action);
      state.variantCartData.pendingRequests = Math.max(
        0,
        Number(state.variantCartData.pendingRequests || 0) - 1,
      );
      if (cartRequest) {
        decrementMerchantPendingRequests(state, merchantId);
      }
      state.loading = state.variantCartData.pendingRequests > 0;
      state.variantCartData.loading = state.variantCartData.pendingRequests > 0;
      if (latestRequest) {
        state.variantData = action.payload;
        state.error = '';
        state.variantCartData.error = '';
        upsertVariantCartData(state, action.payload);
        delete state.variantCartData.latestRequestIdByKey[
          createVariantRequestKey(action.meta?.arg)
        ];
      }
    });
    builder.addCase(fetchVariantData.rejected, (state, action) => {
      const merchantId = getRequestMerchantId(action);
      if (merchantId && state.currentMerchantId !== merchantId) return;

      const latestRequest = isLatestVariantRequest(state, action);
      const cartRequest = isCartVariantRequest(action);
      const errorMessage =
        action.payload?.message ||
        action.error?.message ||
        'Unable to retrieve this product option';
      state.variantCartData.pendingRequests = Math.max(
        0,
        Number(state.variantCartData.pendingRequests || 0) - 1,
      );
      if (cartRequest) {
        decrementMerchantPendingRequests(state, merchantId);
      }
      state.loading = state.variantCartData.pendingRequests > 0;
      state.variantCartData.loading = state.variantCartData.pendingRequests > 0;
      if (latestRequest) {
        state.variantData = null;
        state.error = errorMessage;
        state.variantCartData.error = errorMessage;
      }
      if (latestRequest && cartRequest && merchantId) {
        if (!state.variantCartData.errorsByMerchant) {
          state.variantCartData.errorsByMerchant = {};
        }
        state.variantCartData.errorsByMerchant[merchantId] = errorMessage;
      }
      if (latestRequest) {
        delete state.variantCartData.latestRequestIdByKey[
          createVariantRequestKey(action.meta?.arg)
        ];
      }
    });
  },
});

export const { cacheVariantDetail, setVariantMerchantContext } =
  variantSlice.actions;

export default variantSlice.reducer;
